import { useEffect, useRef } from "react";
import { AXIS_KEYS } from "../axes";
import t, { LOCALE } from "../locales";

const SIZE = 500;
const CENTER = SIZE / 2;
const LABEL_GUTTER = 64;
const RADIUS = CENTER - LABEL_GUTTER;
const RING_COUNT = 5;
const LABEL_PADDING = 8;

const BOUNCE_KEYFRAMES = [
  { transform: "scale(1)" },
  { transform: "scale(1.02)", offset: 0.4 },
  { transform: "scale(1)" },
];
const BOUNCE_TIMING = { duration: 450, easing: "ease-in-out" };

const PERCENT = new Intl.NumberFormat(LOCALE, { style: "percent" });

function round(value) {
  return Math.round(value * 100) / 100;
}

function pointAt(index, radius) {
  const radians = (index / AXIS_KEYS.length) * 2 * Math.PI - Math.PI / 2;

  return [
    round(CENTER + radius * Math.cos(radians)),
    round(CENTER + radius * Math.sin(radians)),
  ];
}

function polygonPath(radii) {
  const commands = radii.map((radius, index) => {
    const [x, y] = pointAt(index, radius);

    return `${index === 0 ? "M" : "L"}${x},${y}`;
  });

  return `${commands.join("")}Z`;
}

function labelAttributes(index) {
  const [x, y] = pointAt(index, RADIUS);
  const horizontal = Math.sign(x - CENTER);
  const vertical = Math.sign(y - CENTER);

  return {
    x: x + horizontal * LABEL_PADDING,
    y: y + vertical * LABEL_PADDING,
    textAnchor: ["end", "middle", "start"][horizontal + 1],
    dominantBaseline: ["auto", "middle", "hanging"][vertical + 1],
  };
}

export default function SpiderChart({ layers, bounceOn }) {
  const areas = layers.map((layer) => ({
    ...layer,
    d: polygonPath(layer.values.map((value) => value * RADIUS)),
  }));

  const pathElements = useRef(new Map());
  const bounces = useRef(new Map());
  const shape = areas.map((area) => area.d).join("");
  const bouncedShape = useRef(shape);

  useEffect(() => {
    if (bouncedShape.current === shape) return;
    bouncedShape.current = shape;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    pathElements.current.forEach((path, label) => {
      bounces.current.get(label)?.cancel();
      bounces.current.set(label, path.animate(BOUNCE_KEYFRAMES, BOUNCE_TIMING));
    });
  }, [bounceOn]);

  return (
    <div className="spider-chart">
      <svg
        className="spider-chart__canvas"
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        aria-hidden="true"
      >
        <g className="spider-chart__grid">
          {Array.from({ length: RING_COUNT }, (_, ring) => (
            <path
              key={ring}
              className="spider-chart__ring"
              d={polygonPath(
                AXIS_KEYS.map(() => (RADIUS / RING_COUNT) * (ring + 1)),
              )}
            />
          ))}
          {AXIS_KEYS.map((key, index) => {
            const [x, y] = pointAt(index, RADIUS);

            return (
              <line
                key={key}
                className="spider-chart__spoke"
                x1={CENTER}
                y1={CENTER}
                x2={x}
                y2={y}
              />
            );
          })}
        </g>

        <g className="spider-chart__layers">
          {areas.map((layer) => (
            <path
              key={layer.label}
              ref={(node) => {
                if (node) pathElements.current.set(layer.label, node);
                else pathElements.current.delete(layer.label);
              }}
              className="spider-chart__area"
              style={{ color: layer.color }}
              d={layer.d}
            />
          ))}
        </g>

        <g className="spider-chart__labels">
          {AXIS_KEYS.map((key, index) => (
            <text
              key={key}
              className="spider-chart__label"
              {...labelAttributes(index)}
            >
              {t(`spider-chart.axes.${key}`)}
            </text>
          ))}
        </g>
      </svg>

      {areas.length > 1 && (
        <ul className="spider-chart__legend" role="list">
          {areas.map((layer) => (
            <li key={layer.label} className="spider-chart__legend-item">
              <span
                className="spider-chart__legend-icon"
                style={{ color: layer.color }}
              />
              {layer.label}
            </li>
          ))}
        </ul>
      )}

      <table className="sr-only">
        <caption>{t("spider-chart.screen-reader-table.caption")}</caption>
        <thead>
          <tr>
            <th scope="col">
              {t("spider-chart.screen-reader-table.character")}
            </th>
            {AXIS_KEYS.map((key) => (
              <th key={key} scope="col">
                {t(`spider-chart.axes.${key}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {areas.map((layer) => (
            <tr key={layer.label}>
              <th scope="row">{layer.label}</th>
              {AXIS_KEYS.map((key, index) => (
                <td key={key}>{PERCENT.format(layer.values[index])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
